import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdExpandMore } from 'react-icons/md';
import {
  buildAdminClientGroups,
  formatAdminClientOptionLabel,
  formatAdminCompanyGroupLabel,
} from '../utils/adminClientGroups';
import './AdminClientSelector.css';

const normalizeId = (value) => (value == null ? '' : String(value));

const formatLastMessageAge = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  const parsed = new Date(timestamp);
  const time = parsed.getTime();
  if (!Number.isFinite(time)) {
    return '';
  }

  const diffMs = Date.now() - time;
  if (diffMs < 0) {
    return 'now';
  }

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;

  if (diffMs < minute) {
    return 'now';
  }
  if (diffMs < hour) {
    return '<1h';
  }
  if (diffMs < day) {
    return `${Math.floor(diffMs / hour)}h`;
  }
  if (diffMs < week) {
    return `${Math.floor(diffMs / day)}d`;
  }
  if (diffMs < month) {
    return `${Math.floor(diffMs / week)}w`;
  }
  if (diffMs < year) {
    return `${Math.floor(diffMs / month)}mon`;
  }
  return `${Math.floor(diffMs / year)}y`;
};

const AdminClientSelector = ({
  clients,
  currentUserId,
  selectedClient,
  onClientChange,
  onRefreshClients,
  includeEmail = false,
  variant = 'sidebar',
}) => {
  const [open, setOpen] = useState(false);
  const [refreshedClients, setRefreshedClients] = useState(null);
  const rootRef = useRef(null);
  const selectedClientId = normalizeId(selectedClient);
  const displayClients = refreshedClients || clients;

  const groups = useMemo(
    () => buildAdminClientGroups(displayClients, currentUserId),
    [displayClients, currentUserId]
  );

  const selectedClientRecord = useMemo(() => (
    Array.isArray(displayClients)
      ? displayClients.find((client) => normalizeId(client?.id) === selectedClientId)
      : null
  ), [displayClients, selectedClientId]);

  const selectedLabel = selectedClientRecord
    ? formatAdminClientOptionLabel(selectedClientRecord, { includeEmail })
    : 'Select a client';
  const selectedLastMessageAge = formatLastMessageAge(selectedClientRecord?.last_user_message_at);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const selectClient = (clientId) => {
    onClientChange?.(normalizeId(clientId));
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const toggleOpen = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) {
      Promise.resolve(onRefreshClients?.())
        .then((nextClients) => {
          if (Array.isArray(nextClients)) {
            setRefreshedClients(nextClients);
          }
        })
        .catch((error) => {
          console.warn('Failed to refresh admin clients:', error);
        });
    }
  };

  const renderClientOption = (client, className = '') => {
    const clientId = normalizeId(client?.id);
    const selected = clientId === selectedClientId;
    const lastMessageAge = formatLastMessageAge(client?.last_user_message_at);
    return (
      <button
        key={clientId}
        type="button"
        className={`admin-client-selector-option ${selected ? 'is-selected' : ''} ${className}`.trim()}
        onClick={() => selectClient(clientId)}
        role="option"
        aria-selected={selected}
        title={formatAdminClientOptionLabel(client, { includeEmail: true })}
      >
        <span className="admin-client-selector-option-name">
          {formatAdminClientOptionLabel(client, { includeEmail })}
        </span>
        {lastMessageAge && (
          <span
            className="admin-client-selector-option-age"
            title={`Last user message: ${new Date(client.last_user_message_at).toLocaleString()}`}
          >
            {lastMessageAge}
          </span>
        )}
      </button>
    );
  };

  const renderCompanyGroup = (group, keyPrefix = '') => (
    <React.Fragment key={`${keyPrefix}${group.key}`}>
      <div className="admin-client-selector-company" title={formatAdminCompanyGroupLabel(group)}>
        {formatAdminCompanyGroupLabel(group)}
      </div>
      {group.clients.map((client) => renderClientOption(client, 'is-company-user'))}
    </React.Fragment>
  );

  return (
    <div
      ref={rootRef}
      className={`admin-client-selector admin-client-selector--${variant}`}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className="admin-client-selector-trigger"
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="admin-client-selector-trigger-label">
          <span>{selectedLabel}</span>
          {selectedLastMessageAge && (
            <span
              className="admin-client-selector-trigger-age"
              title={`Last user message: ${new Date(selectedClientRecord.last_user_message_at).toLocaleString()}`}
            >
              {selectedLastMessageAge}
            </span>
          )}
        </span>
        <MdExpandMore size={16} />
      </button>

      {open && (
        <div className="admin-client-selector-menu" role="listbox">
          {groups.mainUser && (
            <>
              <div className="admin-client-selector-section">Main User</div>
              {renderClientOption(groups.mainUser)}
            </>
          )}

          {groups.subscriberCompanies.length > 0 && (
            <div className="admin-client-selector-section">Subscribers</div>
          )}
          {groups.subscriberCompanies.map((group) => renderCompanyGroup(group))}

          {groups.demoCompanies.length > 0 && (
            <div className="admin-client-selector-section">Demo users</div>
          )}
          {groups.demoCompanies.map((group) => renderCompanyGroup(group, 'demo-'))}

          {groups.restCompanies.length > 0 && (
            <div className="admin-client-selector-section">Rest</div>
          )}
          {groups.restCompanies.map((group) => renderCompanyGroup(group, 'rest-'))}

          {groups.unassignedUsers.length > 0 && (
            <>
              <div className="admin-client-selector-section">Unassigned users</div>
              {groups.unassignedUsers.map((client) => renderClientOption(client))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminClientSelector;
